/**
 * Theme: the shared horizontal rail.
 *
 * Arrows and the scroll indicator for every `.hp-rail` in the store. The card
 * bookmarks are NOT here: see assets/core-card-save.js. Written once so the home
 * rails, the collection rows and the search results reuse it with no edits: it
 * binds to the <core-rail> element, never to a page, a section id or a rail id.
 *
 * WHY A CUSTOM ELEMENT RATHER THAN A querySelectorAll SWEEP.
 * The PDP rails arrive through the Section Rendering API, morphed in by
 * assets/product-recommendations.js long after DOMContentLoaded. A sweep at
 * load finds nothing, and a MutationObserver on the whole document to catch it
 * is a heavier tool than the one the platform already provides.
 * connectedCallback fires whenever an <core-rail> enters the DOM, however it
 * got there.
 *
 * EVERY LISTENER IS DELEGATED ONTO THE HOST, and every handler re-resolves the
 * rail, the track and the thumb when it runs. morphSection can replace the
 * whole subtree while keeping the host, which would strand any handler bound
 * to a child. Nothing here holds a reference to a child across a tick.
 *
 * The bookmarks on the cards inside a rail are handled by
 * assets/core-card-save.js at document level, so a card behaves identically
 * here, on the wishlist grid and on any surface built later.
 */
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('core-rail')) return;

  /** The concept's gap, and the fallback if the computed value is unreadable. */
  var GAP_FALLBACK = 20;

  /** Slack, in px, either side of the scroll extremes. The concept's own. */
  var EDGE = 2;

  /** The indicator's floor, so a very long rail still shows a grabbable thumb. */
  var MIN_THUMB_PERCENT = 8;

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * THE CARD BOOKMARK IS NOT HANDLED HERE ANY MORE.
   *
   * The toggle, the painting and the wishlist:change repaint all moved to
   * assets/core-card-save.js, bound at document level, because a bookmark
   * delegated onto this host was silently dead on any surface without a rail:
   * the wishlist grid, and soon the collection grid and search. The rails now
   * get the behaviour from the same document handler as everything else, so
   * there is one implementation rather than two that can drift.
   */

  class CoreRail extends HTMLElement {
    connectedCallback() {
      // A morph can disconnect and reconnect the host. Bind once, then just
      // re-measure: the handlers below survive a subtree replacement.
      if (!this.__coreBound) {
        this.__coreBound = true;

        this.__onClick = this.#onClick.bind(this);
        this.__onScroll = this.#schedule.bind(this);
        this.__onResize = this.#schedule.bind(this);
        this.__onPointerDown = this.#onPointerDown.bind(this);
        this.__onPointerMove = this.#onPointerMove.bind(this);
        this.__onPointerUp = this.#onPointerUp.bind(this);

        this.addEventListener('click', this.__onClick);
        this.addEventListener('scroll', this.__onScroll, true);
        this.addEventListener('pointerdown', this.__onPointerDown);
        this.addEventListener('pointermove', this.__onPointerMove);
        this.addEventListener('pointerup', this.__onPointerUp);
        this.addEventListener('pointercancel', this.__onPointerUp);

        // The children arrive in one morph, so childList on the subtree is
        // enough; we do not need attributes or character data.
        this.__mutations = new MutationObserver(this.__onResize);
        // `hidden` is watched too: a rail that filters its cards by toggling the
        // attribute (the multi-buy section) changes its scroll width without
        // adding or removing a child. attributeFilter keeps this to that one
        // attribute, so every other rail sees exactly the records it always did.
        this.__mutations.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden'],
        });
      }

      window.addEventListener('resize', this.__onResize);

      this.#schedule();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.__onResize);
    }

    // ---- element lookups, always fresh ------------------------------------

    get #rail() {
      return this.querySelector('[data-rail-scroller]');
    }

    get #track() {
      return this.querySelector('[data-rail-track]');
    }

    // ---- measurement -------------------------------------------------------

    /** Coalesce scroll, resize and mutation bursts into one frame. */
    #schedule() {
      if (this.__frame) return;
      this.__frame = window.requestAnimationFrame(() => {
        this.__frame = 0;
        this.#refresh();
      });
    }

    /**
     * Two cards plus their gap, the concept's step.
     *
     * MEASURES THE FIRST VISIBLE CHILD, NOT THE FIRST .pc. It used to look for
     * .pc, which is the product card class, so any rail of something else fell
     * through to the viewport-percentage fallback. The category rail on the
     * home page is tiles, not product cards, and it wants the same two-item
     * step as everything else.
     *
     * IDENTICAL ON A PRODUCT RAIL BY CONSTRUCTION: every child there is a .pc
     * and none is hidden, so the first visible child IS the first .pc, which is
     * exactly what the old selector resolved to.
     *
     * [hidden] IS SKIPPED DELIBERATELY. The category rail keeps both
     * departments in the DOM and hides the inactive one, so the first child in
     * document order can be a display:none tile whose width is 0. Measuring
     * that would give a 40px step and arrows that barely move.
     */
    #step(rail) {
      var card = null;
      var children = rail.children;
      for (var i = 0; i < children.length; i++) {
        if (!children[i].hasAttribute('hidden')) {
          card = children[i];
          break;
        }
      }
      if (!card) return rail.clientWidth * 0.8;
      var gap = parseFloat(window.getComputedStyle(rail).columnGap);
      if (!isFinite(gap)) gap = GAP_FALLBACK;
      var pitch = card.getBoundingClientRect().width + gap;
      // data-rail-page steps by the cards WHOLLY visible (at least one) rather
      // than two. Only the multi-buy section sets it; every other rail is
      // unchanged.
      if (this.hasAttribute('data-rail-page')) {
        return Math.max(1, Math.floor((rail.clientWidth + gap) / pitch)) * pitch;
      }
      return pitch * 2;
    }

    #refresh() {
      var rail = this.#rail;
      if (!rail) return;

      var max = rail.scrollWidth - rail.clientWidth;

      var buttons = this.querySelectorAll('[data-rail-dir]');
      for (var i = 0; i < buttons.length; i++) {
        var forward = Number(buttons[i].getAttribute('data-rail-dir')) > 0;
        buttons[i].disabled = forward ? rail.scrollLeft >= max - EDGE : rail.scrollLeft <= EDGE;
      }

      var track = this.#track;
      if (!track) return;

      // Nothing to scroll: no dead track, and no thumb to drag nowhere.
      if (max <= EDGE) {
        track.hidden = true;
        return;
      }
      track.hidden = false;

      var thumb = track.querySelector('span');
      if (!thumb) return;
      var width = Math.max((rail.clientWidth / rail.scrollWidth) * 100, MIN_THUMB_PERCENT);
      thumb.style.width = width + '%';
      thumb.style.left = (rail.scrollLeft / max) * (100 - width) + '%';
    }

    // ---- interaction -------------------------------------------------------

    #onClick(event) {
      // No bookmark branch. assets/core-card-save.js owns that click, at
      // document level, for every surface including this one.
      var arrow = event.target.closest('[data-rail-dir]');
      if (!arrow || !this.contains(arrow)) return;

      var rail = this.#rail;
      if (!rail) return;

      rail.scrollBy({
        left: this.#step(rail) * Number(arrow.getAttribute('data-rail-dir')),
        behavior: motionQuery.matches ? 'auto' : 'smooth',
      });
    }

    // ---- dragging the indicator -------------------------------------------

    #seek(clientX) {
      var rail = this.#rail;
      var track = this.#track;
      if (!rail || !track) return;

      var thumb = track.querySelector('span');
      var box = track.getBoundingClientRect();
      var thumbWidth = thumb ? thumb.getBoundingClientRect().width : 0;

      // Centre the thumb under the pointer, so the grab point does not jump.
      var ratio = (clientX - box.left - thumbWidth / 2) / Math.max(1, box.width - thumbWidth);
      ratio = Math.max(0, Math.min(1, ratio));
      rail.scrollLeft = ratio * (rail.scrollWidth - rail.clientWidth);
    }

    #onPointerDown(event) {
      var track = event.target.closest('[data-rail-track]');
      if (!track || !this.contains(track)) return;
      this.__dragging = true;
      try {
        track.setPointerCapture(event.pointerId);
      } catch (error) {
        // Capture is a convenience; dragging still works without it.
      }
      this.#seek(event.clientX);
      event.preventDefault();
    }

    #onPointerMove(event) {
      if (this.__dragging) this.#seek(event.clientX);
    }

    #onPointerUp() {
      this.__dragging = false;
    }
  }

  customElements.define('core-rail', CoreRail);
})();
